import {
  FormCheckbox,
  FormError,
  FormGroupLabel,
  FormInput,
  FormLabel,
  FormRadio,
  FormRadioGroup,
  useFormContext,
  type FormCheckboxProps,
  type FormInputProps,
  type FormRadioProps,
} from "@ariakit/react";
import { cx, MinWidth, useMediaQuery } from "@indietabletop/appkit";
import { useId, useRef, type ReactNode } from "react";
import check from "./check.svg";
import { divider, field, option } from "./style.css.ts";

export type FormTextFieldProps = FormInputProps & {
  label: string;
  hint?: ReactNode;
  autoFocusOnWide?: boolean;
};

export function FormTextField(props: FormTextFieldProps) {
  const isWide = useMediaQuery(MinWidth.WIDE);
  const { name, label, hint, autoFocusOnWide, autoFocus, ...inputProps } =
    props;
  const finalAutoFocus = autoFocus || (isWide && autoFocusOnWide);

  return (
    <div className={field.container}>
      <div>
        <FormLabel name={name} className={field.label}>
          {label}
        </FormLabel>

        <FormInput
          {...inputProps}
          name={name}
          className={field.input()}
          autoFocus={finalAutoFocus}
          data-1p-ignore
        />
      </div>

      <FormError name={name} className={field.issue} />

      {hint && <div className={field.hint}>{hint}</div>}
    </div>
  );
}

export function ReadonlyTextField(props: {
  label: string;
  value: string;
  hint?: string;
  error?: string;
}) {
  const id = useId();
  const { label, hint, error, ...inputProps } = props;

  return (
    <div className={field.container}>
      <div>
        <label htmlFor={id} className={field.label}>
          {label}
        </label>

        <input
          id={id}
          {...inputProps}
          className={field.input({ style: "body" })}
          readOnly
          data-1p-ignore
        />
      </div>

      <div className={field.issue}>{error}</div>

      {hint && <div className={field.hint}>{hint}</div>}
    </div>
  );
}

export type FormOptionProps = Pick<
  FormRadioProps & FormCheckboxProps,
  "name" | "disabled"
> & {
  onChange?: () => void;
  value: string;
  label: string;
  description?: ReactNode;
  info?: ReactNode;
  type?: "radio" | "checkbox";
};

export function FormOption(props: FormOptionProps) {
  const {
    label,
    info,
    description,
    type = "radio",
    disabled,
    name,
    value,
    ...optionProps
  } = props;
  const id = useId();
  const ref = useRef<HTMLInputElement | null>(null);
  const OptionComponent = type === "checkbox" ? FormCheckbox : FormRadio;
  const form = useFormContext();

  const handleLabelClick = () => {
    const current = form?.getValue<string | string[]>(name);

    // Focus the input element so that tabbing order works correctly.
    ref.current?.focus();

    // Call any potential additional handlers attached to this field
    optionProps.onChange?.();

    if (Array.isArray(current)) {
      const index = current.findIndex((v) => v === value);

      if (index >= 0) {
        form?.setValue(name, [
          ...current.slice(0, index),
          ...current.slice(index + 1),
        ]);
      } else {
        form?.setValue(name, [...current, value]);
      }
    } else {
      if (type === "radio") {
        form?.setValue(name, value);
      } else {
        const nextValue = current === value ? "" : value;
        form?.setValue(name, nextValue);
      }
    }
  };

  return (
    <div className={option.container}>
      <OptionComponent
        {...optionProps}
        ref={ref}
        aria-labelledby={id}
        value={value}
        name={name}
        disabled={disabled}
        render={<div className={option.checkboxContainer} />}
      >
        <div className={option.checkbox}>
          <img src={check} alt="" className={option.check} />
        </div>
      </OptionComponent>

      {/* Using FormLabel with array values is currently buggy with Ariakit */}
      <div id={id} className={option.labelContainer} onClick={handleLabelClick}>
        <div className={option.label}>{label}</div>

        {info && <div className={option.aside}>{info}</div>}
      </div>

      {description && <div className={option.description}>{description}</div>}
    </div>
  );
}

export function Divider() {
  return <div className={divider.line} />;
}

export function FormOptionsGroup(props: {
  label: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
}) {
  const { label, aside, children } = props;

  return (
    <FormRadioGroup {...cx(field.container)}>
      <FormGroupLabel {...cx(field.label)}>
        <span {...cx(field.labelMain)}>{label}</span>
        {aside && <span {...cx(field.labelAside)}>{aside}</span>}
      </FormGroupLabel>
      {children}
    </FormRadioGroup>
  );
}
